import { Component, Suspense, use, useEffect, type ComponentType, type ReactElement, type ReactNode } from "react";
import { matchRoutes, useLocation, useRoutes, type RouteObject } from "react-router-dom";
import {
  LOCALES,
  ROUTES,
  POST_PREFIX,
  CASE_PREFIX,
  SOLUTION_PREFIX,
  INDUSTRY_PREFIX,
  HTML_LANG,
  localeFromPath,
} from "./i18n/config";
import Home from "./pages/Home/Home";
import { loadPostBody } from "./data/posts";
import FloatingButtons from "./components/FloatingButtons/FloatingButtons";
import CookieConsent from "./components/CookieConsent/CookieConsent";
import { scrollToSection } from "./services/scrollToSection";
import {
  getSeo,
  buildJsonLd,
  serializeJsonLd,
  SITE_URL,
  SITE_NAME,
  SITE_LOCALE,
  OG_IMAGE,
  JSONLD_SCRIPT_ID,
  isNoindexPath,
} from "./seo";

function upsertMetaDescription(content: string) {
  let meta = document.querySelector<HTMLMetaElement>(
    'meta[name="description"]'
  );

  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.appendChild(meta);
  }

  meta.content = content;
}

function upsertCanonical(pathname: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }

  link.href = `${SITE_URL}${pathname}`;
}

function upsertMetaTag(
  attr: "property" | "name",
  key: string,
  content: string
) {
  let meta = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attr, key);
    document.head.appendChild(meta);
  }

  meta.content = content;
}

// Reemplaza el JSON-LD de la ruta anterior al navegar dentro de la SPA. Sin
// esto, una navegacion cliente dejaria los datos estructurados de la pagina
// previa, describiendo mal la pagina actual.
function upsertJsonLd(pathname: string) {
  let script = document.getElementById(
    JSONLD_SCRIPT_ID,
  ) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = JSONLD_SCRIPT_ID;
    document.head.appendChild(script);
  }

  script.textContent = serializeJsonLd(buildJsonLd(pathname));
}

function upsertSocialTags(title: string, description: string, url: string) {
  // Open Graph (Facebook, LinkedIn, WhatsApp, etc.)
  upsertMetaTag("property", "og:type", "website");
  upsertMetaTag("property", "og:site_name", SITE_NAME);
  upsertMetaTag("property", "og:locale", SITE_LOCALE);
  upsertMetaTag("property", "og:title", title);
  upsertMetaTag("property", "og:description", description);
  upsertMetaTag("property", "og:url", url);
  upsertMetaTag("property", "og:image", OG_IMAGE);

  // Twitter Card
  upsertMetaTag("name", "twitter:card", "summary_large_image");
  upsertMetaTag("name", "twitter:title", title);
  upsertMetaTag("name", "twitter:description", description);
  upsertMetaTag("name", "twitter:image", OG_IMAGE);
}

type RouteParams = Record<string, string | undefined>;

/**
 * Pagina que se baja bajo demanda. A diferencia de React.lazy, una vez cargada
 * se renderiza en forma sincronica: renderToString (el prerender) no puede
 * esperar un modulo, y la hidratacion no puede mostrar un fallback donde el
 * HTML ya trae la pagina. Por eso el cliente y el prerender esperan
 * `preload()` antes de renderizar (ver preloadRoute).
 *
 * `id` es el archivo fuente de la pagina tal como figura en el manifest de
 * Vite: el prerender lo usa para sumar al HTML el modulepreload del chunk de la
 * pagina (y falla si el id no existe, asi un archivo movido no pasa de largo).
 */
interface LazyPage<P extends object> {
  (props: P): ReactElement;
  id: string;
  preload: () => Promise<unknown>;
}

/** Espera antes de volver a pedir el chunk de una pagina que no llego. */
const RETRY_FAILED_CHUNK_MS = 5_000;

function lazyPage<P extends object>(
  id: string,
  load: () => Promise<{ default: ComponentType<P> }>,
): LazyPage<P> {
  let component: ComponentType<P> | undefined;
  let pending: Promise<ComponentType<P>> | undefined;
  let failedAt = 0;

  // La promesa lleva su estado anotado (status/value, el formato que lee
  // `use()`): asi `use()` sigue de largo cuando ya se resolvio, en vez de
  // suspender otra vez.
  const preload = () => {
    // Un chunk que fallo se vuelve a pedir recien pasado un rato: mientras tanto
    // se devuelve la misma promesa rechazada, que es lo que `use()` tiene que
    // leer para tirar el error al ChunkBoundary. Si se la descartara al
    // instante, cada render pediria el chunk de nuevo, sin parar.
    if (pending && failedAt && Date.now() - failedAt > RETRY_FAILED_CHUNK_MS) {
      pending = undefined;
      failedAt = 0;
    }

    if (!pending) {
      const promise = load().then((module) => {
        component = module.default;
        return module.default;
      });
      const tracked = promise as { status?: string; value?: unknown; reason?: unknown };
      promise.then(
        (value) => {
          tracked.status = "fulfilled";
          tracked.value = value;
        },
        (reason) => {
          tracked.status = "rejected";
          tracked.reason = reason;
          failedAt = Date.now();
        },
      );
      pending = promise;
    }
    return pending;
  };

  function Page(props: P) {
    use(preload());
    const Component = component!;
    return <Component {...props} />;
  }

  return Object.assign(Page, { id, preload });
}

// Todas las paginas menos la home: quien entra por la home no baja el codigo
// de las demas (soluciones, casos, el panel interno...).
const AboutUs = lazyPage("src/pages/AboutUs/AboutUs.tsx", () => import("./pages/AboutUs/AboutUs"));
const Enterprise = lazyPage("src/pages/Enterprise/Enterprise.tsx", () => import("./pages/Enterprise/Enterprise"));
const CasesOverview = lazyPage("src/pages/Cases/CasesOverview.tsx", () => import("./pages/Cases/CasesOverview"));
const CaseIndex = lazyPage("src/pages/Cases/CaseIndex.tsx", () => import("./pages/Cases/CaseIndex"));
const CasePage = lazyPage("src/pages/Cases/CasePage.tsx", () => import("./pages/Cases/CasePage"));
const SolutionsIndex = lazyPage("src/pages/Solutions/SolutionsIndex.tsx", () => import("./pages/Solutions/SolutionsIndex"));
const SolutionPage = lazyPage("src/pages/Solutions/SolutionPage.tsx", () => import("./pages/Solutions/SolutionPage"));
const IndustriesIndex = lazyPage("src/pages/Industries/IndustriesIndex.tsx", () => import("./pages/Industries/IndustriesIndex"));
const IndustryPage = lazyPage("src/pages/Industries/IndustryPage.tsx", () => import("./pages/Industries/IndustryPage"));
const News = lazyPage("src/pages/News/News.tsx", () => import("./pages/News/News"));
const NewsPost = lazyPage("src/pages/News/NewsPost.tsx", () => import("./pages/News/NewsPost"));
const NotFound = lazyPage("src/pages/NotFound/NotFound.tsx", () => import("./pages/NotFound/NotFound"));
const Panel = lazyPage("src/pages/Panel/Panel.tsx", () => import("./pages/Panel/Panel"));
const ProposalPage = lazyPage("src/pages/Proposals/ProposalPage.tsx", () => import("./pages/Proposals/ProposalPage"));
const ContractPage = lazyPage("src/pages/Contracts/ContractPage.tsx", () => import("./pages/Contracts/ContractPage"));

interface PageHandle {
  page: LazyPage<never>;
  /** Datos que la pagina necesita ya en el primer render, ademas de su codigo. */
  preloadData?: (params: RouteParams) => Promise<unknown>;
}

function pageRoute<P extends object>(
  path: string,
  Page: LazyPage<P>,
  props?: P,
  preloadData?: PageHandle["preloadData"],
): RouteObject {
  return {
    path,
    element: <Page {...(props as P)} />,
    handle: { page: Page, preloadData } satisfies PageHandle,
  };
}

// Las rutas se generan desde el mapa de i18n: cada pagina existe en los dos
// idiomas y las URLs salen de una sola fuente de verdad, asi que no pueden
// desincronizarse del selector, el sitemap ni los hreflang.
const routes: RouteObject[] = [
  ...LOCALES.flatMap((locale): RouteObject[] => [
    { path: ROUTES.home[locale], element: <Home /> },
    pageRoute(ROUTES.about[locale], AboutUs),
    pageRoute(ROUTES.enterprise[locale], Enterprise),
    // Indice general: junta los tres rubros en una sola pagina. Las categorias
    // de abajo son los indices de tarjetas de cada rubro; cada caso tiene su
    // propia vista en /caso/:slug (mas abajo, el contenido de los casos es
    // solo en español).
    pageRoute(ROUTES.cases[locale], CasesOverview),
    pageRoute(ROUTES.ecommerceCases[locale], CaseIndex, { rubro: "ecommerce" }),
    pageRoute(ROUTES.softwareCases[locale], CaseIndex, { rubro: "software-factory" }),
    pageRoute(ROUTES.aiCases[locale], CaseIndex, { rubro: "ai" }),
    pageRoute(ROUTES.news[locale], News),
    pageRoute(`${POST_PREFIX[locale]}/:slug`, NewsPost, undefined, ({ slug }) =>
      slug ? loadPostBody(slug) : Promise.resolve(),
    ),
    // Detalle de caso: /caso/:slug en español, /en/case/:slug en ingles. El
    // mismo CasePage resuelve el contenido segun el idioma.
    pageRoute(`${CASE_PREFIX[locale]}/:slug`, CasePage),
    // Landings de solucion, por intencion de busqueda especifica (ver
    // src/data/solutions.ts). El indice vive en la misma ruta base
    // (ROUTES.solutions); el detalle usa :slug.
    pageRoute(ROUTES.solutions[locale], SolutionsIndex),
    pageRoute(`${SOLUTION_PREFIX[locale]}/:slug`, SolutionPage),
    // Paginas de industria: "como aplicamos esto a tu rubro" (ver
    // src/data/industries.ts). Mismo patron que soluciones.
    pageRoute(ROUTES.industries[locale], IndustriesIndex),
    pageRoute(`${INDUSTRY_PREFIX[locale]}/:slug`, IndustryPage),
  ]),
  // Herramienta interna, no contenido del sitio: una sola ruta, sin version en
  // ingles ni entrada en el selector de idioma.
  pageRoute("/panel", Panel),
  // Propuestas comerciales protegidas por contraseña, una por cliente. Tampoco
  // tienen version en ingles: el slug lo define el equipo de WAIA al crear
  // cada propuesta desde el panel, no el visitante.
  pageRoute("/propuestas/:slug", ProposalPage),
  // Contratos que un cliente aprueba escribiendo su nombre: misma idea que las
  // propuestas (slug y contraseña los define el equipo desde el panel), sin
  // version en ingles.
  pageRoute("/contratos/:slug", ContractPage),
  pageRoute("*", NotFound),
];

/**
 * Carga lo que la ruta necesita para renderizar en forma sincronica: el codigo
 * de su pagina y, en una nota, el cuerpo. Lo esperan el cliente antes de
 * hidratar (src/entry-client.tsx) y el prerender antes de cada render
 * (scripts/prerender.mjs). Devuelve los `id` de las paginas que matchearon.
 */
// eslint-disable-next-line react-refresh/only-export-components
export async function preloadRoute(pathname: string): Promise<string[]> {
  const handles = (matchRoutes(routes, pathname) ?? [])
    .map((match) => ({ handle: match.route.handle as PageHandle | undefined, params: match.params }))
    .filter((entry) => entry.handle !== undefined);

  await Promise.all(
    handles.flatMap(({ handle, params }) => [handle!.page.preload(), handle!.preloadData?.(params)]),
  );

  return handles.map(({ handle }) => handle!.page.id);
}

interface ChunkBoundaryProps {
  children: ReactNode;
  /** Cuando cambia, el error anterior se descarta (el visitante navego a otra ruta). */
  resetKey: string;
  locale: "es" | "en";
}

/**
 * Red de seguridad para una pagina cuyo chunk no se pudo bajar (se publico una
 * version nueva con la pestaña abierta, o se corto la red) cuando ya se uso el
 * unico reload automatico de entry-client. Sin esto React desmonta toda la raiz
 * y queda una pantalla en blanco sin salida. Usa links y un boton comunes (no
 * del router): una carga de pagina completa es justo lo que hace falta.
 */
class ChunkBoundary extends Component<ChunkBoundaryProps, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidUpdate(prev: ChunkBoundaryProps) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;

    const en = this.props.locale === "en";
    return (
      <main id="main-content" style={{ padding: "120px 24px", textAlign: "center", color: "#fff", background: "#0a0a0a", minHeight: "100vh" }}>
        <h1 style={{ fontSize: "1.5rem", margin: "0 0 12px" }}>
          {en ? "We couldn't load this page" : "No pudimos cargar esta página"}
        </h1>
        <p style={{ margin: "0 0 24px" }}>
          {en ? "Check your connection and try again." : "Revisá tu conexión y probá de nuevo."}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ padding: "14px 28px", borderRadius: 999, border: 0, background: "#fff", color: "#0a0a0a", fontWeight: 600, fontSize: "1rem", minHeight: 44 }}
        >
          {en ? "Reload" : "Recargar"}
        </button>
        <p style={{ marginTop: 24 }}>
          <a href={en ? "/en" : "/"} style={{ color: "#fff" }}>
            {en ? "Go to the home page" : "Ir al inicio"}
          </a>
        </p>
      </main>
    );
  }
}

function App() {
  const location = useLocation();
  const locale = localeFromPath(location.pathname);
  const seo = getSeo(location.pathname);
  const routeElement = useRoutes(routes);

  useEffect(() => {
    document.title = seo.title;
  }, [seo.title]);

  useEffect(() => {
    upsertMetaDescription(seo.description);
  }, [seo.description]);

  useEffect(() => {
    upsertCanonical(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    upsertSocialTags(
      seo.title,
      seo.description,
      `${SITE_URL}${location.pathname}`
    );
  }, [seo.title, seo.description, location.pathname]);

  useEffect(() => {
    upsertJsonLd(location.pathname);
  }, [location.pathname]);

  // El prerender ya lo pone para /panel (ver renderHeadTags en seo.ts), pero
  // una navegacion SPA -sin recarga de pagina- no vuelve a pasar por ahi:
  // sin este efecto, ir de la home a /panel sin F5 dejaria la etiqueta puesta
  // solo por casualidad (la que trajo la pagina anterior), y volver de /panel
  // a una pagina publica la dejaria marcada como noindex por error.
  useEffect(() => {
    upsertMetaTag(
      "name",
      "robots",
      isNoindexPath(location.pathname)
        ? "noindex, nofollow"
        : "index, follow",
    );
  }, [location.pathname]);

  // El <html lang> tiene que seguir al idioma de la ruta: al navegar de /
  // a /en dentro de la SPA no hay recarga, y sin esto el HTML seguiria
  // declarando es-AR mientras muestra ingles.
  useEffect(() => {
    document.documentElement.lang = HTML_LANG[locale];
  }, [locale]);

  useEffect(() => {
    // Comparar contra "/" a secas dejaba /en sin scroll por hash: la home del
    // ingles es /en, no /.
    const isHome = location.pathname === ROUTES.home[locale];

    if (isHome) {
      if (location.hash) {
        const sectionId = location.hash.slice(1);

        // scrollToSection (src/services/scrollToSection.ts) desactiva el
        // scroll-snap de <html> mientras dura el scroll puntual -si no, lo
        // va "corrigiendo" de vuelta al origen antes de llegar, ver el
        // comentario en ese archivo-. setTimeout(0): el destino recien
        // termina de montarse en este mismo commit (navegacion entre
        // paginas), hace falta un tick para que exista en el DOM.
        const timeoutId = window.setTimeout(() => scrollToSection(sectionId), 0);
        return () => window.clearTimeout(timeoutId);
      }

      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    if (location.hash) {
      // Un enlace con ancla en otra pagina (los saltos de /casos, #case-story)
      // va a su destino y no al tope: antes se forzaba el tope y el ancla se
      // perdia. El destino puede tardar unos cuadros en montarse (chunk de la
      // ruta), asi que se reintenta un rato antes de rendirse.
      let targetId = location.hash.slice(1);
      try {
        targetId = decodeURIComponent(targetId);
      } catch {
        // Un "%" suelto o cortado a la mitad ("#50%") no es un escape valido:
        // se usa el texto tal cual en vez de romper la pagina.
      }
      let frames = 0;
      let rafId = 0;
      const goToTarget = () => {
        const target = document.getElementById(targetId);
        if (target) {
          target.scrollIntoView({ block: "start", behavior: "instant" as ScrollBehavior });
          return;
        }
        if (++frames < 40) {
          rafId = window.requestAnimationFrame(goToTarget);
        } else {
          window.scrollTo({ top: 0, behavior: "auto" });
        }
      };
      rafId = window.requestAnimationFrame(goToTarget);
      return () => window.cancelAnimationFrame(rafId);
    }

    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.hash, location.pathname, locale]);

  return (
    <>
      <a href="#main-content" className="skip-link">
        {locale === "en" ? "Skip to main content" : "Saltar al contenido principal"}
      </a>
      {/* Sin fallback visible: la pagina de la ruta actual ya viene cargada
          (preloadRoute, antes de hidratar), y en una navegacion dentro del
          sitio React Router mantiene la pagina anterior en pantalla hasta que
          la nueva esta lista. */}
      <ChunkBoundary resetKey={location.pathname} locale={locale}>
        <Suspense fallback={null}>{routeElement}</Suspense>
      </ChunkBoundary>

      {/* Fuera de las Routes: el "Dejanos tu consulta" y el WhatsApp estan en
          todo el sitio (antes FloatingButtons vivia solo dentro de Home),
          salvo /panel -herramienta interna, no tiene sentido ofrecer contacto
          comercial ni el aviso de cookies de marketing ahi-.
          key=pathname fuerza un remount limpio en cada cambio de pagina, para
          que el observer de footer/#contacto no arrastre los nodos -y el
          estado- de la pagina anterior. */}
      {location.pathname !== "/panel" &&
        !location.pathname.startsWith("/propuestas/") &&
        !location.pathname.startsWith("/contratos/") && (
        <>
          <FloatingButtons key={location.pathname} />

          {/* Sin key={location.pathname}: a diferencia de FloatingButtons, el
              aviso de cookies no depende de la ruta y no debe reaparecer al
              navegar dentro de la SPA una vez que el visitante ya eligio. */}
          <CookieConsent />
        </>
      )}
    </>
  );
}

export default App;
